import React, { useState, useRef } from 'react';
import { 
  Plus, 
  Trash2, 
  Download, 
  Send, 
  Calculator,
  Building,
  Phone,
  Mail,
  MapPin,
  FileText,
  User,
  Hash
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const EBill = () => {
  const billRef = useRef();
  
  // Predefined article suggestions
  const articleSuggestions = [
    'Single Patam Door Frame',
    'Double Patam Door Frame',
    'Single Patam Window Frame',
    'Double Patam Window Frame',
    'Single Patam Ventilation',
    'Double Patam Ventilation',
    'Steel Gate Frame',
    'Window Grill',
    'Security Door Frame',
    'Balcony Railing',
    'Staircase Railing',
    'Custom Steel Work'
  ];

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    mobile: '',
    address: ''
  });

  // Product image catalog (categorised)
  // Product catalog with model numbers
  const productCatalog = {
    Door: [
      { src: '/images/products/1.png', model: 'D-101' },
      { src: '/images/products/2.png', model: 'D-102' },
      { src: '/images/products/3.png', model: 'D-103' },
    ],
    Window: [
      { src: '/images/products/4.png', model: 'W-201' },
      { src: '/images/products/5.png', model: 'W-202' },
    ],
    Ventilation: [
      { src: '/images/products/6.png', model: 'V-301' },
      { src: '/images/products/7.png', model: 'V-302' },
    ]
  };

  const [billItems, setBillItems] = useState([
    { 
      id: 1, 
      article: '', 
      quantity: 1, 
      price: 0,
      steelPipeLength: '',
      giPipeLength: '',
      smallHinges: '',
      largeHinges: '',
      showSuggestions: false,
      productImage: '',
      imageSearch: ''
    }
  ]);

  const [billNumber, setBillNumber] = useState("");
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);

  // Pricing rates
  const [rates, setRates] = useState({
    steelPipePerFeet: 60,
    giPipePerFeet: 110,
    smallHingePrice: 40,
    largeHingePrice: 50
  });

  // Company Information
  const companyInfo = {
    name: 'Happy Steels',
    mobile: '+91- 9919100155, 9919100150',
    email: 'akyenterprises@happysteels.in',
    gst: '09IGHPD3256E1ZF',
    address: 'Kherauna Bypass, Antu Road Amethi, Uttar Pradesh - 227405',
    website: 'www.happysteels.in'
  };

  const handleCustomerChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  // Helper to get full email for display and sending
  const getFullEmail = () => {
    if (!customerInfo.email) return '';
    // If already contains @, return as is, else append @gmail.com
    return customerInfo.email.includes('@') ? customerInfo.email : `${customerInfo.email}@gmail.com`;
  };

  const handleRateChange = (field, value) => {
    setRates(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    // Recalculate all item prices when rates change
    setBillItems(prev => prev.map(item => ({
      ...item,
      price: calculateItemPrice({
        ...item,
        steelPipeLength: item.steelPipeLength || 0,
        giPipeLength: item.giPipeLength || 0,
        smallHinges: item.smallHinges || 0,
        largeHinges: item.largeHinges || 0
      }, {
        ...rates,
        [field]: parseFloat(value) || 0
      })
    })));
  };

  const calculateItemPrice = (item, currentRates = rates) => {
    const steelPipeCost = (item.steelPipeLength || 0) * currentRates.steelPipePerFeet;
    const giPipeCost = (item.giPipeLength || 0) * currentRates.giPipePerFeet;
    const smallHingesCost = (item.smallHinges || 0) * currentRates.smallHingePrice;
    const largeHingesCost = (item.largeHinges || 0) * currentRates.largeHingePrice;
    
    return steelPipeCost + giPipeCost + smallHingesCost + largeHingesCost;
  };

  const handleItemChange = (id, field, value) => {
    setBillItems(prev => 
      prev.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: field === 'price' ? parseFloat(value) || 0 : value };
          // Auto-calculate price if it's a measurement field
          if (['steelPipeLength', 'giPipeLength', 'smallHinges', 'largeHinges'].includes(field)) {
            updatedItem.price = calculateItemPrice(updatedItem);
          }
          // Hide suggestions if article is set
          if (field === 'article') {
            updatedItem.showSuggestions = false;
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const handleArticleFocus = (id) => {
    setBillItems(prev => prev.map(item => item.id === id ? { ...item, showSuggestions: true } : item));
  };

  const handleArticleBlur = (id) => {
    setTimeout(() => {
      setBillItems(prev => prev.map(item => item.id === id ? { ...item, showSuggestions: false } : item));
    }, 150); // Delay to allow click
  };

  const handleArticleSuggestionClick = (id, suggestion) => {
    setBillItems(prev => prev.map(item => item.id === id ? { ...item, article: suggestion, showSuggestions: false } : item));
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      article: '',
      quantity: 1,
      price: 0,
      steelPipeLength: '',
      giPipeLength: '',
      smallHinges: '',
      largeHinges: '',
      productImage: '',
      imageSearch: ''
    };
    setBillItems(prev => [...prev, newItem]);
  };
  // Handler to set image search for a bill item
  const handleImageSearchChange = (id, value) => {
    setBillItems(prev => prev.map(item => item.id === id ? { ...item, imageSearch: value } : item));
  };
  // Handler to set product image for a bill item
  const handleProductImageChange = (id, image) => {
    setBillItems(prev => prev.map(item => item.id === id ? { ...item, productImage: image } : item));
  };

  const removeItem = (id) => {
    if (billItems.length > 1) {
      setBillItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const calculateTotal = () => {
    return billItems.reduce((total, item) => {
      return total + (parseFloat(item.quantity) * parseFloat(item.price || 0));
    }, 0);
  };

  const grandTotal = calculateTotal();

  const generatePDF = async () => {
    const element = billRef.current;
    let scale = 2;
    let quality = 1.0;
    let pdfBlob = null;
    let pdf = null;
    let imgData = null;
    let fileSizeMB = 0;
    let attempts = 0;
    const maxAttempts = 4;
    const minQuality = 0.5;
    const maxFileSizeMB = 2;
    let fileName = `HS-${billNumber}-${customerInfo.name || 'Customer'}.pdf`;

    while (attempts < maxAttempts) {
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      imgData = canvas.toDataURL('image/jpeg', quality);
      pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Get the PDF as a Blob to check size
      pdfBlob = pdf.output('blob');
      fileSizeMB = pdfBlob.size / (1024 * 1024);
      if (fileSizeMB <= maxFileSizeMB) {
        break;
      }
      // Reduce quality and scale for next attempt
      quality = Math.max(minQuality, quality - 0.2);
      scale = Math.max(1, scale - 0.5);
      attempts++;
    }

    if (fileSizeMB > maxFileSizeMB) {
      alert('PDF could not be compressed below 2 MB. Try reducing content or contact support.');
      return;
    }
    pdf.save(fileName);
  };

  const sendBillToCustomer = async () => {
    // Download the invoice PDF first
    await generatePDF();
    // Then simulate sending email
    alert(`Bill HS-${billNumber} will be sent to ${getFullEmail() || '[no email provided]'}`);
    // In real implementation, you would integrate with an email service
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16 transition-colors duration-300">
      {/* Simple Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">E-Bill Generator</h1>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Create professional invoices for steel fabrication</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* First Panel - Input Forms (1/3 width on large screens) */}
          <div className="lg:col-span-1 lg:max-w-sm w-full mx-auto space-y-6">
            {/* Pricing Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4 transition-colors duration-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center transition-colors duration-300">
                <Calculator className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                Pricing Setup
              </h3>
              
              <div className="space-y-3 flex flex-col w-full">
                <div className="flex items-center justify-between w-full gap-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Steel Pipe (₹/ft)</label>
                  <input
                    type="number"
                    value={rates.steelPipePerFeet}
                    onChange={(e) => handleRateChange('steelPipePerFeet', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300 text-center"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex items-center justify-between w-full gap-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Frame (GI) (₹/ft)</label>
                  <input
                    type="number"
                    value={rates.giPipePerFeet}
                    onChange={(e) => handleRateChange('giPipePerFeet', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300 text-center"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex items-center justify-between w-full gap-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Small Hinge (₹/pc)</label>
                  <input
                    type="number"
                    value={rates.smallHingePrice}
                    onChange={(e) => handleRateChange('smallHingePrice', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300 text-center"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="flex items-center justify-between w-full gap-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Large Hinge (₹/pc)</label>
                  <input
                    type="number"
                    value={rates.largeHingePrice}
                    onChange={(e) => handleRateChange('largeHingePrice', e.target.value)}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300 text-center"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4 transition-colors duration-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center transition-colors duration-300">
                <User className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                Customer Details
              </h3>
              
              <div className="space-y-4">
                {/* Invoice Details */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-300">
                  <div className="flex flex-col md:flex-row md:gap-4 gap-3">
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">Invoice Number</label>
                      <div className="flex min-w-0">
                        <span className="inline-flex items-center px-3 text-sm text-gray-900 dark:text-white bg-gray-200 dark:bg-gray-600 border border-r-0 border-gray-300 dark:border-gray-500 rounded-l-md transition-colors duration-300">
                          HS-
                        </span>
                        <input
                          type="text"
                          value={billNumber}
                          onChange={(e) => {
                            // Only allow up to 3 digits, numbers only
                            const val = e.target.value.replace(/\D/g, "").slice(0, 3);
                            setBillNumber(val);
                          }}
                          maxLength={3}
                          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                          placeholder="001"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors duration-300">Date</label>
                      <input
                        type="date"
                        value={billDate}
                        onChange={(e) => setBillDate(e.target.value)}
                        className="w-full min-w-0 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">Customer Name *</label>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => handleCustomerChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="Enter customer name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">Email *</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={customerInfo.email}
                      onChange={(e) => handleCustomerChange('email', e.target.value.replace(/@.*/, ''))}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                      placeholder="customer"
                    />
                    <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300 text-sm select-none">@gmail.com</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">Mobile</label>
                  <input
                    type="tel"
                    value={customerInfo.mobile}
                    onChange={(e) => handleCustomerChange('mobile', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="+91 98765 43210"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">Address</label>
                  <textarea
                    value={customerInfo.address}
                    onChange={(e) => handleCustomerChange('address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={generatePDF}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center font-medium"
              >
                <Download className="h-5 w-5 mr-2" />
                Download PDF
              </button>
              
              <button
                onClick={sendBillToCustomer}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center font-medium"
                // Button is always enabled (active)
              >
                <Send className="h-5 w-5 mr-2" />
                Send to Customer
              </button>
            </div>
          </div>

          {/* Second Panel - Bill Items (2/3 width on large screens) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4 transition-colors duration-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center transition-colors duration-300">
                <FileText className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                Bill Items ({billItems.length})
              </h3>
            </div>

            {/* Individual Item Tiles */}
            {billItems.map((item, index) => (
              <div key={item.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4 transition-colors duration-300">
                {/* Item Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-600 dark:bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 transition-colors duration-300">
                      {item.article || `Item #${index + 1}`}
                    </h4>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors duration-200"
                    disabled={billItems.length === 1}
                    title="Remove item"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Article Name & Product Image */}
                <div className="mb-4 relative flex flex-col md:flex-row md:items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">Article Name</label>
                    <input
                      type="text"
                      value={item.article}
                      onChange={(e) => handleItemChange(item.id, 'article', e.target.value)}
                      onFocus={() => handleArticleFocus(item.id)}
                      onBlur={() => handleArticleBlur(item.id)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                      placeholder="Enter article name"
                      autoComplete="off"
                    />
                    {item.showSuggestions && articleSuggestions.filter(s => s.toLowerCase().includes((item.article || '').toLowerCase())).length > 0 && (
                      <ul className="absolute z-10 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-auto">
                        {articleSuggestions.filter(s => s.toLowerCase().includes((item.article || '').toLowerCase())).map((suggestion, idx) => (
                          <li
                            key={idx}
                            className="px-4 py-2 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900 text-gray-800 dark:text-gray-100"
                            onMouseDown={() => handleArticleSuggestionClick(item.id, suggestion)}
                          >
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-2 min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Add Image</label>
                    <input
                      type="text"
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs mb-1"
                      placeholder="Search by model no."
                      value={item.imageSearch || ''}
                      onChange={e => handleImageSearchChange(item.id, e.target.value)}
                    />
                    <select
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                      value={item.productImage || ''}
                      onChange={e => handleProductImageChange(item.id, e.target.value)}
                    >
                      <option value="">Select</option>
                      {Object.entries(productCatalog).map(([cat, arr]) => {
                        // Filter by model number or category
                        const filtered = arr.filter(imgObj =>
                          !item.imageSearch ||
                          imgObj.model.toLowerCase().includes(item.imageSearch.toLowerCase()) ||
                          cat.toLowerCase().includes(item.imageSearch.toLowerCase())
                        );
                        if (!filtered.length) return null;
                        return (
                          <optgroup key={cat} label={cat}>
                            {filtered.map((imgObj, idx) => (
                              <option key={imgObj.src} value={imgObj.src}>
                                {cat} {idx + 1} ({imgObj.model})
                              </option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </select>
                    {/* Show preview of selected image or preview of hovered option */}
                    {item.productImage && (
                      <div className="mt-2 flex flex-col items-center">
                        <img
                          src={item.productImage}
                          alt="Product Preview"
                          className="w-20 h-20 object-contain border rounded shadow bg-gray-100"
                          style={{ background: '#f3f4f6' }}
                        />
                        <span className="text-xs text-gray-500 mt-1">Preview</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Materials Section */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 transition-colors duration-300">Materials & Components</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-300">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Steel Pipe (ft)</label>
                      <input
                        type="number"
                        value={item.steelPipeLength}
                        onChange={(e) => handleItemChange(item.id, 'steelPipeLength', parseFloat(e.target.value) || 0)}
                        className="w-1/2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white transition-colors duration-300"
                        min="0"
                        step="0.1"
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-300">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Frame (GI) (ft)</label>
                      <input
                        type="number"
                        value={item.giPipeLength}
                        onChange={(e) => handleItemChange(item.id, 'giPipeLength', parseFloat(e.target.value) || 0)}
                        className="w-1/2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-600 text-gray-900 dark:text-white transition-colors duration-300"
                        min="0"
                        step="0.1"
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-300">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Small Hinges</label>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleItemChange(item.id, 'smallHinges', Math.max(0, (item.smallHinges || 0) - 1))}
                          className="w-10 h-10 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white rounded-lg text-lg font-bold flex items-center justify-center transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.smallHinges || 0}
                          onChange={(e) => handleItemChange(item.id, 'smallHinges', Math.max(0, parseInt(e.target.value) || 0))}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center bg-white dark:bg-gray-600 text-gray-900 dark:text-white transition-colors duration-300"
                          min="0"
                        />
                        <button
                          type="button"
                          onClick={() => handleItemChange(item.id, 'smallHinges', (item.smallHinges || 0) + 1)}
                          className="w-10 h-10 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white rounded-lg text-lg font-bold flex items-center justify-center transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-300">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Large Hinges</label>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleItemChange(item.id, 'largeHinges', Math.max(0, (item.largeHinges || 0) - 1))}
                          className="w-10 h-10 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white rounded-lg text-lg font-bold flex items-center justify-center transition-colors"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.largeHinges || 0}
                          onChange={(e) => handleItemChange(item.id, 'largeHinges', Math.max(0, parseInt(e.target.value) || 0))}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-center bg-white dark:bg-gray-600 text-gray-900 dark:text-white transition-colors duration-300"
                          min="0"
                        />
                        <button
                          type="button"
                          onClick={() => handleItemChange(item.id, 'largeHinges', (item.largeHinges || 0) + 1)}
                          className="w-10 h-10 bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white rounded-lg text-lg font-bold flex items-center justify-center transition-colors"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Quantity and Price Section */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 transition-colors duration-300">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 transition-colors duration-300">Quantity & Pricing</label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Quantity</label>
                      <select
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 1)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors duration-300"
                      >
                        {[...Array(50)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 transition-colors duration-300">Unit Price</label>
                      <input
                        type="number"
                        value={item.price.toFixed(2)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-white transition-colors duration-300"
                        readOnly
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">Total Amount</label>
                      <div className="px-3 py-2 text-sm bg-green-100 border border-green-200 rounded-lg font-bold text-green-800">
                        ₹{(item.quantity * item.price).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Add Item Button */}
            <div className="flex justify-center">
              <button
                onClick={addItem}
                className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white px-6 py-3 rounded-lg transition-colors duration-200 flex items-center font-medium shadow-sm"
              >
                <Plus className="h-5 w-5 mr-2" />
                Add New Item
              </button>
            </div>

            {/* Total Summary */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4 transition-colors duration-300">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 transition-colors duration-300">Bill Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-base">
                  <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Subtotal:</span>
                  <span className="font-semibold text-gray-900 dark:text-white transition-colors duration-300">₹{calculateTotal().toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-3 transition-colors duration-300">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-gray-900 dark:text-white transition-colors duration-300">Grand Total:</span>
                    <span className="text-blue-600 dark:text-blue-400 transition-colors duration-300">₹{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bill Preview Section - Full Width at Bottom */}
        <div className="mt-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 overflow-hidden transition-colors duration-300">
            <div
              ref={billRef}
              className="bg-white mx-auto"
              style={{
                width: '210mm',
                minHeight: '297mm',
                maxWidth: '100%',
                boxShadow: '0 0 8px 0 rgba(0,0,0,0.08)',
                padding: '0',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '0.5rem',
                background: 'white',
                margin: '0 auto'
              }}
            >
              {/* Invoice Header */}
              <div className="bg-gray-800 p-4">
                <div className="flex items-center justify-between text-white">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center overflow-hidden">
                      <img
                        src="/images/logo.png"
                        alt="Company Logo"
                        className="h-10 w-10 object-contain"
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentNode.innerHTML += '<svg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\' class=\'h-10 w-10 text-gray-300\'><rect width=\'100%\' height=\'100%\' rx=\'4\' fill=\'#e5e7eb\'/><text x=\'50%\' y=\'55%\' text-anchor=\'middle\' fill=\'#9ca3af\' font-size=\'10\'>Logo</text></svg>'; }}
                      />
                    </div>
                    <div>
                      <h1 className="text-xl font-bold">{companyInfo.name}</h1>
                      <p className="text-gray-300 text-sm">Quality Steel Solutions</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-lg font-bold">INVOICE</h2>
                    <p className="text-sm">#HS-{billNumber}</p>
                    <p className="text-xs text-gray-300">{new Date(billDate).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              {/* Company & Customer Information */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Company Info */}
                  <div className="bg-blue-50 rounded-lg p-3">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center text-sm">
                      <Building className="h-4 w-4 mr-2 text-blue-600" />
                      From
                    </h3>
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="flex items-center">
                        <Phone className="h-3 w-3 mr-2 text-blue-500" />
                        <span>{companyInfo.mobile}</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-3 w-3 mr-2 text-blue-500" />
                        <span>{companyInfo.email}</span>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="h-3 w-3 mr-2 mt-0.5 text-blue-500" />
                        <span className="leading-tight">{companyInfo.address}</span>
                      </div>
                      <div className="flex items-center">
                        <Hash className="h-3 w-3 mr-2 text-blue-500" />
                        <span>GST: {companyInfo.gst}</span>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="bg-green-50 rounded-lg p-3">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center text-sm">
                      <User className="h-4 w-4 mr-2 text-green-600" />
                      To
                    </h3>
                    <div className="space-y-1 text-xs text-gray-700">
                      <div className="font-semibold text-gray-800">
                        {customerInfo.name || 'Customer Name'}
                      </div>
                      {customerInfo.email && (
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-2 text-green-500" />
                          <span>{getFullEmail()}</span>
                        </div>
                      )}
                      {customerInfo.mobile && (
                        <div className="flex items-center">
                          <Phone className="h-3 w-3 mr-2 text-green-500" />
                          <span>{customerInfo.mobile}</span>
                        </div>
                      )}
                      {customerInfo.address && (
                        <div className="flex items-start">
                          <MapPin className="h-3 w-3 mr-2 mt-0.5 text-green-500" />
                          <span className="leading-tight">{customerInfo.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="mb-4">
                  <div className="bg-gray-50 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-700 text-white">
                          <tr>
                            <th className="px-2 py-2 text-left">S.No</th>
                            <th className="px-2 py-2 text-left">Article</th>
                            <th className="px-2 py-2 text-center">Image</th>
                            <th className="px-1 py-2 text-center">Steel</th>
                            <th className="px-1 py-2 text-center">Steel ₹</th>
                            <th className="px-1 py-2 text-center">Frame (GI)</th>
                            <th className="px-1 py-2 text-center">GI ₹</th>
                            <th className="px-1 py-2 text-center">S.H</th>
                            <th className="px-1 py-2 text-center">S.H ₹</th>
                            <th className="px-1 py-2 text-center">L.H</th>
                            <th className="px-1 py-2 text-center">L.H ₹</th>
                            <th className="px-1 py-2 text-center">Qty</th>
                            <th className="px-2 py-2 text-right">Price</th>
                            <th className="px-2 py-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {billItems.map((item, index) => {
                            const steelPrice = (item.steelPipeLength || 0) * rates.steelPipePerFeet;
                            const giPrice = (item.giPipeLength || 0) * rates.giPipePerFeet;
                            const shPrice = (item.smallHinges || 0) * rates.smallHingePrice;
                            const lhPrice = (item.largeHinges || 0) * rates.largeHingePrice;
                            return (
                              <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-2 py-2 font-medium">{index + 1}</td>
                                <td className="px-2 py-2">{item.article || '-'}</td>
                                <td className="px-2 py-2 text-center">
                                  {item.productImage ? (
                                    <img
                                      src={item.productImage}
                                      alt="Product"
                                      className="w-10 h-10 object-contain mx-auto border rounded"
                                      style={{ background: '#f3f4f6' }}
                                    />
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-1 py-2 text-center">{item.steelPipeLength || '-'}</td>
                                <td className="px-1 py-2 text-center">₹{steelPrice ? steelPrice.toFixed(2) : '-'}</td>
                                <td className="px-1 py-2 text-center">{item.giPipeLength || '-'}</td>
                                <td className="px-1 py-2 text-center">₹{giPrice ? giPrice.toFixed(2) : '-'}</td>
                                <td className="px-1 py-2 text-center">{item.smallHinges || '-'}</td>
                                <td className="px-1 py-2 text-center">₹{shPrice ? shPrice.toFixed(2) : '-'}</td>
                                <td className="px-1 py-2 text-center">{item.largeHinges || '-'}</td>
                                <td className="px-1 py-2 text-center">₹{lhPrice ? lhPrice.toFixed(2) : '-'}</td>
                                <td className="px-1 py-2 text-center font-medium">{item.quantity}</td>
                                <td className="px-2 py-2 text-right">₹{parseFloat(item.price || 0).toFixed(2)}</td>
                                <td className="px-2 py-2 text-right font-semibold text-green-700">
                                  ₹{(parseFloat(item.quantity) * parseFloat(item.price || 0)).toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Bill Summary */}
                <div className="flex justify-end mb-4">
                  <div className="w-64">
                    <div className="bg-gray-100 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal:</span>
                        <span className="font-semibold">₹{calculateTotal().toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-300 pt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span>Grand Total:</span>
                          <span className="text-blue-600">₹{grandTotal.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 pt-3 text-center">
                  <div className="bg-gray-100 rounded p-2">
                    <p className="text-gray-600 text-xs font-medium">
                      Thank you for your business! | {companyInfo.website}
                    </p>
                    <p className="text-gray-500 text-xs">
                      Professional steel fabrication services
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EBill;
